import * as swcHelpers from '@swc/helpers';
import React__default from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { MediaQuery, toast, Button } from '@grandvisionhq/common';
import { StoreFiltersModal } from '@grandvisionhq/fun-store-filters';
import { usePlacePredictions, mapPlacePredictionToSuggestion } from '@grandvisionhq/locations';
import { useIntl, useFeatureToggles, useClickAndReserveFeatureConfig } from '@grandvisionhq/state';
import { useSessionStorage, useDebouncedEffect } from '@grandvisionhq/utils';
import { storeSearchSubmitEvent } from '../../data-layer/actions.js';
import useStoreFinderUrlParams from './hooks/use-store-finder-url-params.js';
import StoreSearchForm from './StoreSearchForm.js';
import { useStoreFinderContext } from '../../context/StoreFinder/context.js';
import usePlaceById from '../../hooks/api/use-place-by-id.js';
import useStoresSearchHook from '../../hooks/api/use-stores-search-hook.js';
import { getAddressFromSuggestions } from '../../utils/get-address-from-suggestions.js';
import { replaceStoreFinderUrlWithNewQueryParams } from '../../utils/store-utils.js';

const scrollToFormHandlerId = 'location-search-form';
const MIN_SEARCH_CHARACTER_COUNT = 2;
const StoreSearchFormContainer = (_param)=>{
    var { appointmentTypeId , minSearchCharacterCount =MIN_SEARCH_CHARACTER_COUNT , storeQueryFilter , storeQueryForceDistance =true  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "appointmentTypeId",
        "minSearchCharacterCount",
        "storeQueryFilter",
        "storeQueryForceDistance"
    ]);
    const { getLabel  } = useIntl();
    const { config , searchQuery , isPristineSearch , suggestions , isLocationSelected , userLocation , isUsingUserLocation , isUserLocationLoading , useUserLocationError , setIsUserLocationRequested , clearSearch , setSelectedLocation , setSuggestions , setIsPristineSearch , setStores , setSearchQuery , isStoresLoading , setIsStoresLoading , sku , stores , events , setActiveMapInfoWindow , appliedStoreFilters , appliedStoreFilterCount , toggleAppliedStoreFilter , clearAppliedStoreFilters ,  } = useStoreFinderContext();
    const mappedAppliedStoreFilters = React__default.useMemo(()=>Object.entries(appliedStoreFilters).flatMap(([filterName, options])=>options.map((option)=>({
                    name: filterName,
                    value: option
                })
            )
        )
    , [
        appliedStoreFilters
    ]);
    const searchRef = /*#__PURE__*/ React__default.createRef();
    const { featureConfigs  } = useFeatureToggles();
    const maxSearchStoresResults = (featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.maxSearchStoresResults) !== undefined ? featureConfigs.maxSearchStoresResults : 30;
    const { withUrlParams , storeFilter  } = config;
    const isMobile = MediaQuery.useBreakpoint() === MediaQuery.BreakpointName.PHONE;
    const clickAndReserveMode = useClickAndReserveFeatureConfig();
    const [isModalOpen, setModalOpen] = React__default.useState(false);
    const closeModal = ()=>setModalOpen(false)
    ;
    const openModal = ()=>setModalOpen(true)
    ;
    const [isWaitingForSuggestions, setIsWaitingForSuggestions] = React__default.useState(false);
    const [getPlacePredictions, placePredictions] = usePlacePredictions({
        variables: {
            name: searchQuery
        },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
        onError: (error)=>{
            toast.failed('stores.page.search.getPlacePredictions.error');
            console.error('Failed to fetch place predictions: ', error);
        }
    });
    const [getPlaceById, placeById] = usePlaceById({
        id: ''
    });
    const useStoresSearch = useStoresSearchHook(sku);
    const [searchStores, searchStoresResults] = useStoresSearch({
        input: {
            searchGeoLocation: {
                lat: '',
                lon: ''
            },
            filters: mappedAppliedStoreFilters,
            forceDistance: storeQueryForceDistance
        },
        sku: sku !== null && sku !== void 0 ? sku : ''
    });
    var _results;
    const searchedStores = React__default.useMemo(()=>(_results = searchStoresResults.data.results) !== null && _results !== void 0 ? _results : []
    , [
        searchStoresResults
    ]);
    var _filters, ref2;
    // Keep track of the store filters while the data is loading, to prevent
    // the filter modal from being destroyed on filtering
    const storeFilters = React__default.useMemo(()=>{
        var ref1;
        return (ref2 = (_filters = searchStoresResults.data.filters) !== null && _filters !== void 0 ? _filters : (ref1 = searchStoresResults.previousData) === null || ref1 === void 0 ? void 0 : ref1.storesSearch.filters) !== null && ref2 !== void 0 ? ref2 : undefined;
    }, [
        searchStoresResults
    ]);
    const [userLocationInSessionStorage, setUserLocationInSessionStorage] = useSessionStorage('grandvision_user_location', undefined, (value)=>value === undefined
    );
    const labels = React__default.useMemo(()=>({
            placeholder: getLabel('stores.page.search.searchForm.placeholder'),
            noResults: getLabel('stores.page.autocomplete.noResults'),
            useMyCurrentLocation: getLabel('stores.page.search.useMyCurrentLocation'),
            myCurrentLocationPlaceholder: getLabel('stores.page.search.useMyCurrentLocation.placeholder'),
            noAvailableResultsScreenReaderHint: getLabel('elements.autocomplete.noAvailableResultsScreenReaderHint')
        })
    , [
        getLabel
    ]);
    const filterButtonText = appliedStoreFilterCount > 0 ? `${getLabel('stores.storeFilters.cta')} (${appliedStoreFilterCount})` : getLabel('stores.storeFilters.cta');
    const onStoreSearchSubmitEvent = React__default.useCallback(({ location , interaction  })=>{
        const dataLayerEvent = storeSearchSubmitEvent({
            interaction,
            location,
            searchResults: searchedStores.length || null
        });
        DataLayer.pushEvent(dataLayerEvent);
    }, [
        searchedStores
    ]);
    const handleOnChange = React__default.useCallback((value)=>{
        setSearchQuery(value);
        setIsWaitingForSuggestions(false);
        // In case selected location and value in the search field are the same,
        // nothing changed in the input and we can keep selected location
        setSelectedLocation((prevLocation)=>prevLocation === value ? prevLocation : ''
        );
    }, [
        setSearchQuery,
        setSelectedLocation
    ]);
    /** Hook to build store finder URL based on search state,
   *  to handle initial page state on first page load based on query parameters in URL
   */ const newQueryParams = React__default.useMemo(()=>isUsingUserLocation ? {
            userLocation: 'true'
        } : {
            search: searchQuery
        }
    , [
        isUsingUserLocation,
        searchQuery
    ]);
    const initialSearchQuery = useStoreFinderUrlParams({
        handleOnChange,
        setIsUserLocation: setIsUserLocationRequested,
        newQueryParams,
        enabled: withUrlParams
    });
    const getStoresByLocation = React__default.useCallback((id)=>{
        getPlaceById({
            variables: {
                id
            }
        });
        setIsStoresLoading(true);
        const address = getAddressFromSuggestions(suggestions, id);
        setSelectedLocation(address);
        withUrlParams && replaceStoreFinderUrlWithNewQueryParams({
            search: address
        });
        setSuggestions([]);
        if (isPristineSearch) {
            setIsPristineSearch(false);
        }
    }, [
        getPlaceById,
        setSelectedLocation,
        setSuggestions,
        setIsStoresLoading,
        isPristineSearch,
        suggestions,
        setIsPristineSearch,
        withUrlParams, 
    ]);
    const onSuggestionClick = React__default.useCallback((id)=>{
        getStoresByLocation(id);
    }, [
        getStoresByLocation
    ]);
    const onChange = React__default.useCallback((event)=>handleOnChange(event.currentTarget.value)
    , [
        handleOnChange
    ]);
    const onSubmit = React__default.useCallback(()=>{
        setActiveMapInfoWindow(null);
        if (suggestions.length > 0) {
            getStoresByLocation(suggestions[0].id);
        } else {
            setIsWaitingForSuggestions(true);
        }
    }, [
        setActiveMapInfoWindow,
        getStoresByLocation,
        setIsWaitingForSuggestions,
        suggestions
    ]);
    React__default.useEffect(()=>{
        if (isUsingUserLocation) {
            setSearchQuery(labels.myCurrentLocationPlaceholder);
            setSelectedLocation(labels.myCurrentLocationPlaceholder);
        }
    }, [
        isUsingUserLocation,
        labels.myCurrentLocationPlaceholder,
        setSearchQuery,
        setSelectedLocation, 
    ]);
    const handleOnSearch = React__default.useCallback(()=>{
        if (searchQuery.length >= minSearchCharacterCount && !isLocationSelected && !isUsingUserLocation) {
            getPlacePredictions({
                variables: {
                    name: searchQuery
                }
            });
        }
    }, [
        searchQuery,
        minSearchCharacterCount,
        isLocationSelected,
        isUsingUserLocation,
        getPlacePredictions, 
    ]);
    const onUseMyCurrentLocation = React__default.useCallback((clickLocation)=>{
        setIsUserLocationRequested(true);
        setIsWaitingForSuggestions(false);
        if (clickAndReserveMode) {
            var ref;
            events === null || events === void 0 ? void 0 : (ref = events.onClickLocationEvent) === null || ref === void 0 ? void 0 : ref.call(events, clickAndReserveMode, clickLocation);
        }
    }, [
        setIsUserLocationRequested,
        setIsWaitingForSuggestions,
        events,
        clickAndReserveMode
    ]);
    useDebouncedEffect(()=>{
        handleOnSearch();
    }, 500, [
        searchQuery
    ]);
    React__default.useEffect(()=>{
        if (!placePredictions.data) {
            return;
        }
        setSuggestions(mapPlacePredictionToSuggestion(placePredictions.data));
    }, [
        placePredictions.data
    ]);
    React__default.useEffect(()=>{
        if (searchStoresResults.error || placeById.error) {
            setIsStoresLoading(false);
        }
    }, [
        searchStoresResults.error,
        placeById.error,
        setIsStoresLoading
    ]);
    React__default.useEffect(()=>{
        if (useUserLocationError) {
            setSearchQuery('');
            setSelectedLocation('');
            setIsUserLocationRequested(false);
            onStoreSearchSubmitEvent({
                location: null,
                interaction: 'current location'
            });
        }
    }, [
        useUserLocationError,
        setSearchQuery,
        setSelectedLocation,
        setIsUserLocationRequested
    ]);
    React__default.useEffect(()=>{
        if (!placeById.data) {
            return;
        }
        const { coordinates: { lat , lon  } ,  } = placeById.data;
        const deviceGeoLocation = userLocation ? {
            lat: userLocation.lat,
            lon: userLocation.lng
        } : userLocationInSessionStorage && Object.keys(userLocationInSessionStorage).length !== 0 ? {
            lat: userLocationInSessionStorage.lat,
            lon: userLocationInSessionStorage.lng
        } : undefined;
        setIsStoresLoading(true);
        searchStores({
            variables: {
                input: {
                    maxResults: maxSearchStoresResults,
                    searchGeoLocation: {
                        lat,
                        lon
                    },
                    deviceGeoLocation,
                    filters: mappedAppliedStoreFilters,
                    forceDistance: storeQueryForceDistance
                },
                sku: sku !== null && sku !== void 0 ? sku : ''
            }
        });
    // Effect should only run on changing placeById.data or appointmentTypeId
    }, [
        placeById.data,
        storeQueryForceDistance,
        sku,
        mappedAppliedStoreFilters
    ]);
    React__default.useEffect(()=>{
        var ref;
        if (searchedStores.length === 0) {
            return;
        }
        onStoreSearchSubmitEvent({
            location: searchedStores.length > 0 && searchedStores[0].town,
            interaction: isUsingUserLocation && userLocation !== undefined ? 'current location' : 'location typed'
        });
        setStores(searchedStores.filter(storeFilter));
        setIsStoresLoading(false);
        (ref = searchRef.current) === null || ref === void 0 ? void 0 : ref.blur();
        if (isMobile && searchedStores.length > 0) {
            var // Can't use ref in this case because we have two search forms on the page.
            // During the first search ref in useEffect points to the old search field
            // So we need to get needed search form by id.
            // Alternative way ref.current?.scrollIntoView(), but it doesn't work for the first search.
            ref3;
            (ref3 = document.getElementById(scrollToFormHandlerId)) === null || ref3 === void 0 ? void 0 : ref3.scrollIntoView();
        }
    }, [
        searchedStores
    ]);
    React__default.useEffect(()=>{
        if (searchQuery.length < minSearchCharacterCount) {
            setSuggestions([]);
        }
    }, [
        searchQuery,
        minSearchCharacterCount,
        setSuggestions
    ]);
    React__default.useEffect(()=>{
        if (!userLocation) {
            return;
        }
        if (isPristineSearch) {
            setIsPristineSearch(false);
        }
        setUserLocationInSessionStorage(userLocation);
        const { lat , lng: lon  } = userLocation;
        setIsStoresLoading(true);
        searchStores({
            variables: {
                input: {
                    maxResults: maxSearchStoresResults,
                    searchGeoLocation: {
                        lat,
                        lon
                    },
                    deviceGeoLocation: {
                        lat,
                        lon
                    },
                    forceDistance: storeQueryForceDistance,
                    filters: mappedAppliedStoreFilters
                },
                sku: sku !== null && sku !== void 0 ? sku : ''
            }
        });
    }, [
        userLocation,
        appointmentTypeId,
        isPristineSearch,
        storeQueryForceDistance,
        sku,
        mappedAppliedStoreFilters, 
    ]);
    /** Should select first suggestion if already waiting for suggestions list */ React__default.useEffect(()=>{
        if (isWaitingForSuggestions && suggestions.length > 0) {
            getStoresByLocation(suggestions[0].id);
            setIsWaitingForSuggestions(false);
        }
    }, [
        suggestions,
        isWaitingForSuggestions,
        getStoresByLocation,
        setIsWaitingForSuggestions
    ]);
    React__default.useEffect(()=>{
        if (initialSearchQuery === searchQuery && isPristineSearch && suggestions.length > 0) {
            getStoresByLocation(suggestions[0].id);
        }
    }, [
        initialSearchQuery,
        searchQuery,
        isPristineSearch,
        suggestions,
        getStoresByLocation
    ]);
    // Dispatch keyup event on Enter and Space manually after pressing key on Use my location from the dropdown.
    // After pressing Use my location from the dropdown input becomes disabled,
    // when input disabled key events don't trigger on it.
    // Need to reset the keypress state to handle cursor correctly.
    React__default.useEffect(()=>{
        var ref, ref4;
        if (!isUsingUserLocation) {
            return;
        }
        (ref = searchRef.current) === null || ref === void 0 ? void 0 : ref.dispatchEvent(new KeyboardEvent('keyup', {
            code: 'Enter'
        }));
        (ref4 = searchRef.current) === null || ref4 === void 0 ? void 0 : ref4.dispatchEvent(new KeyboardEvent('keyup', {
            code: 'Space'
        }));
    }, [
        isUsingUserLocation,
        searchRef
    ]);
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(StoreSearchForm, swcHelpers.extends({}, rest, {
        searchRef: searchRef,
        labels: labels,
        loading: placePredictions.loading || isUserLocationLoading,
        showClearButton: searchQuery.length > 0,
        searchQuery: searchQuery,
        suggestions: suggestions,
        onChange: onChange,
        onSubmit: onSubmit,
        onClear: clearSearch,
        onSuggestionClick: onSuggestionClick,
        onSuggestionTopContentClick: ()=>onUseMyCurrentLocation('search suggestion')
        ,
        onUseMyCurrentLocationClick: ()=>onUseMyCurrentLocation('button')
        ,
        isSuggestionSelected: isLocationSelected,
        isFocusedOnMount: true,
        isUsingUserLocation: isUsingUserLocation,
        minSearchCharacterCount: minSearchCharacterCount,
        queryResultsFilters: undefined
    })), stores.length ? /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("div", {
        className: "store-search-field-container__filter-button-wrapper"
    }, /*#__PURE__*/ React__default.createElement(Button, {
        caption: filterButtonText,
        variant: "ghost",
        icon: "filter",
        onClick: openModal
    }))) : null, storeFilters ? /*#__PURE__*/ React__default.createElement(StoreFiltersModal, {
        isOpen: isModalOpen,
        onModalClose: closeModal,
        filters: storeFilters,
        appliedFilters: appliedStoreFilters,
        hasAppliedFilters: appliedStoreFilterCount > 0,
        toggleFilter: toggleAppliedStoreFilter,
        resultsCount: searchedStores.length,
        isLoadingResults: isStoresLoading,
        clearFilters: clearAppliedStoreFilters
    }) : null);
};
var StoreSearchFormContainerV2 = StoreSearchFormContainer;

export { StoreSearchFormContainerV2 as default };
//# sourceMappingURL=StoreSearchFormContainerV2.js.map
